import { Helmet } from 'react-helmet-async';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePassportService } from '../../../vodea/utilities/hooks';
import { useSelector } from 'react-redux';
import { RootState } from '../../../stores';
import VuiLoadingScreen from '../../../vodea/@vodea-ui/components/VuiLoadingScreen';
import { Col, Row } from 'react-bootstrap';
import VuiSearch from '../../../vodea/@vodea-ui/components/VuiSearch';
import CustomerRepository from '../../../repositories/CustomerRepository';
import VuiSelect from '../../../vodea/@vodea-ui/components/Forms/VuiSelect';
import CategoryRepository from '../../../repositories/CategoryRepository';
import SalesOrderCartItem from './components/SalesOrderCartItem';
import ProductRepository from '../../../repositories/ProductRepository';
import InfiniteScroll from 'react-infinite-scroll-component';
import { AxiosError, AxiosResponse } from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCartArrowDown, faExclamationCircle, faPlus, faShoppingBag } from '@fortawesome/free-solid-svg-icons';
import ProductItem from './components/ProductItem';
import { LoadingIndication } from '../../../vodea/@vodea-ui/components/DataTableInfiniteScroll';
import _ from 'lodash';
import VuiNumberFormat from '../../../vodea/@vodea-ui/components/VuiNumberFormat';
import ButtonAddNote from './components/ButtonAddNote';
import ButtonAddDisc from './components/ButtonAddDisc';
import ButtonSave from './components/ButtonSave';
import SalesOrderRepository from '../../../repositories/SalesOrderRepository';
import { mapHookErrors, showToast } from '../../../vodea/utilities/helpers/global';
import { VuiButton } from '../../../vodea/@vodea-ui/components/VuiButton';
import useDebounce from '../../../vodea/utilities/hooks/useDebounce';
import ButtonAddCustomer from './components/ButtonAddCustomer';
import ButtonBack from './components/ButtonBack';
import ModalProductItem from './components/ModalProductItem';
import ModalAskPrintSalesOrder from './components/ModalAskPrintSalesOrder';

interface ProductStock {
  id: string;
  storage_id: number;
  quantity: number;
  price: string;
  storage: {
    id: number;
    name: string;
  };
}

interface ProductConversion {
  unit_id: {
    id: number;
    name: string;
    code: string;
  } | null;
  quantity: number | null;
  unit: {
    id: number;
    name: string;
    code: string;
  } | null;
}

export interface IProductData {
  id: number;
  name: string;
  category_id: string;
  category_name: string;
  default_price: number;
  price: number;
  unit_name: string;
  unit_id: number;
  unit_code: string;
  supplier_id: number;
  supplier_name: string;
  quantity: number;
  minimum_stock: number;
  sku: string;
  product_photos: object;
  photo_url: string;
  product_conversions: ProductConversion[];
  product_discounts: {
    quantity: number;
    discount: number;
  }[];
  stocks: ProductStock[];
  product_tiers: {
    id: number;
    product_id: number;
    unit_id: number;
    quantity: number;
    product_tier_prices: {
      id: number;
      product_tier_id: number;
      membership_id: number;
      price: number;
    }[];
  }[];
  default_stock?: ProductStock;
}

export interface ICartItem {
  // key: string;
  total: number;
  discount: number;
  discount_product: number;
  available_stock: number;
  quantity: number;
  price: number;
  storage_id: object | null;
  unit_id: object | null;
  product_id: IProductData;
  total_discount: number;
  product_conversions :ProductConversion[];
}

const SalesOrderSimplePage: React.FC<any> = () => {
  const { t } = useTranslation();
  const title = t('Create Sales Order');
  const { isOnFetchingUser } = usePassportService();
  const loggedIn = useSelector((state: RootState) => state.system.loggedIn);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitSaveLoading, setSubmitSaveLoading] = useState(false);
  const [note, setNote] = useState('');
  const [discount, setDiscount] = useState(0);
  const [search, setSearch] = useState('');
  const [products, setProducts] = useState<IProductData[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isOpenAskPrintSalesOrder, setIsOpenAskPrintSalesOrder] = useState(false);
  const [salesOrderId, setSalesOrderId] = useState(undefined);
  const [pageQuery, setPageQuery] = useState<{
    page: number;
    limit: number;
    customer: any;
    category: any;
  }>({
    page: 1,
    limit: 40,
    customer: null,
    category: null
  });
  const debounceSearch = useDebounce(search, 250);
  const [isOpenSelectUnit, setIsOpenSelectUnit] = useState(false);
  const [carts, setCarts] = useState<ICartItem[]>([]);
  const [selectedData, setSelectedData] = useState<IProductData>(null);

  const handleAddProductToCart = useCallback((item: IProductData) => {
    setSelectedData(item);
    setIsOpenSelectUnit(true);
  }, []);

  const [isFetchingProduct, setIsFetchingProduct] = useState(false);
  const loadProducts = useCallback(async () => {
    if (!pageQuery.customer) return;

    setIsFetchingProduct(true);

    if (pageQuery.page === 1) {
      setProducts([]);
    }

    const params = {
      page: pageQuery.page,
      per_page: pageQuery.limit,
      search,
      category: pageQuery.category?.id || undefined,
      customer: pageQuery.customer?.id || undefined,
      with: [
        'productPhotos',
        'productConversions.unit',
        'productDiscounts',
        'productTiers.unit',
        'productTiers.productTierPrices',
        'category',
        'stocks.storage'
      ]
    };

    await ProductRepository.all(params)
      .then((resp: AxiosResponse) => {
        const responseData = resp.data.data;
        const normalizeData: IProductData[] = responseData.map((product: any) => ({
          id: product.id,
          name: product.name,
          default_price: Number(product?.default_price),
          price: Number(product?.price),
          unit_name: product?.unit_name,
          unit_id: product?.unit_id,
          unit_code: product?.unit_code,
          supplier_id: product?.supplier_id,
          supplier_name: product?.supplier_name,
          quantity: Number(product?.quantity),
          minimum_stock: Number(product?.minimum_stock),
          sku: product?.sku,
          photo_url: _.get(product, 'product_photos[0].url', null),
          product_conversions: product?.product_conversions?.map((pc: any) => ({
            id: pc.id,
            unit_id: Number(pc.unit_id),
            quantity: Number(pc?.quantity),
            unit: pc?.unit
          })),
          product_discounts: product?.product_discounts?.map((pd: any) => ({
            quantity: Number(pd?.quantity),
            discount: Number(pd?.discount)
          })),
          stocks: product?.stocks?.map((s: any) => ({
            id: s.id,
            storage_id: Number(s?.storage_id),
            quantity: Number(s?.quantity),
            price: Number(s?.price),
            storage: s?.storage
          })),
          product_tiers: product.product_tiers.map((pt: any) => ({
            id: pt.id,
            quantity: Number(pt.quantity),
            unit_id: Number(pt.unit_id),
            product_tier_prices: pt.product_tier_prices
          }))
        }));
        setProducts(prevState => [...prevState, ...normalizeData]);
        if (responseData?.length < pageQuery?.limit) {
          setHasMore(false);
        }
      })
      .catch(() => {})
      .finally(() => {
        setIsFetchingProduct(false);
      });
  }, [pageQuery, search]);

  const handleNextProduct = () => {
    setPageQuery({
      page: pageQuery.page + 1,
      limit: pageQuery.limit,
      category: pageQuery.category,
      customer: pageQuery.customer
    });
  };

  const renderSummary = useMemo(() => {
    const subtotal = carts.reduce((acc, value) => acc + value.quantity * value.price, 0);
    const totalDisc = Number(discount) + carts.reduce((acc, value) => acc + value.total_discount + value.discount, 0);
    const grandTotal = carts.reduce((acc, value) => acc + value.total, 0);

    return (
      <div className='cart-summary'>
        <div className='d-flex align-items-center justify-content-between'>
          <div className='cart-summary-title'>Subtotal</div>
          <div className='cart-summary-subtitle'>
            <VuiNumberFormat prefix='IDR ' data={subtotal} value={subtotal} />
          </div>
        </div>
        <div className='d-flex align-items-center justify-content-between'>
          <div className='cart-summary-title'>Total Disc.</div>
          <div className='cart-summary-subtitle'>
            <VuiNumberFormat prefix='IDR ' data={totalDisc} value={totalDisc} />
          </div>
        </div>
        <div className='d-flex align-items-center justify-content-between'>
          <div className='cart-summary-title'>Grand Total</div>
          <div className='cart-summary-subtitle'>
            <VuiNumberFormat prefix='IDR ' data={grandTotal} value={grandTotal} />
          </div>
        </div>
      </div>
    );
  }, [carts, discount]);

  useEffect(() => {
    loadProducts().then();
  }, [pageQuery, debounceSearch]);

  const [errors, setErrors] = useState<any>(null);
  const handleSubmit = useCallback(
    async (payment: number) => {
      const salesOrderItems = carts.map(c => ({
        product_id: c.product_id?.id,
        unit_id: _.get(c.unit_id, 'id', null),
        storage_id: _.get(c.storage_id, 'id', null),
        price: c.price,
        quantity: c.quantity,
        available_stock: c.available_stock,
        discount: c.discount,
        total: c.total,
        discount_product: c.discount_product
      }));

      await SalesOrderRepository.create({
        payment,
        customer_id: pageQuery?.customer?.id,
        note,
        discount: Number(discount),
        sales_order_items: salesOrderItems
      })
        .then((resp) => {
          setSalesOrderId(resp.data.data.id);
          setIsOpenAskPrintSalesOrder(true);
          showToast('Successfully create sales order', 'success');
          handleResetForm();
          setErrors(null);
        })
        .catch((e: AxiosError) => {
          const mapErrors: any = {};
          if (e.response?.data?.errors) {
            const errors = mapHookErrors(e.response?.data?.errors);
            Object.keys(errors).forEach((key: any) => {
              mapErrors[key] = errors[key];
            });
            setErrors(mapErrors);
          }
          showToast(e.message, 'error');
        })
        .finally(() => {
          setSubmitLoading(false);
          setSubmitSaveLoading(false);
        });
    },
    [pageQuery, carts, note, discount]
  );

  const handleOnSave = useCallback(
    (payment: number) => {
      setSubmitSaveLoading(true);
      handleSubmit(Number(payment)).then();
    },
    [pageQuery, carts, note, discount]
  );

  const handleOnPayNow = useCallback(async () => {
    setSubmitLoading(true);
    const grandTotal = carts.reduce((acc, value) => acc + value.total, 0);
    handleSubmit(grandTotal).then();
  }, [pageQuery, carts, note, discount]);

  const handleResetForm = useCallback(() => {
    setPageQuery({
      ...pageQuery,
      page: 1,
      customer: null,
      category: null
    });
    setCarts([]);
    setNote('');
    setDiscount(0);
    setProducts([])
  }, []);

  const handleOnCloseSelectUnit = useCallback(() => {
    setIsOpenSelectUnit(false);
    setSelectedData(null);
  }, []);

  const handleOnSubmitSelectUnit = useCallback((item: IProductData) => {
    setCarts(prevState => [
      ...prevState,
      ...[
        {
          total: item?.price,
          total_discount: 0,
          discount: 0,
          discount_product: 0,
          available_stock: Number(selectedData?.quantity) ?? 0,
          quantity: 1,
          price: item?.price,
          storage_id: selectedData.stocks.find(s => Number(s.quantity) > 0)?.storage ?? null,
          unit_id: {
            id: item.unit_id,
            name: item.unit_name,
            code: item.unit_code
          },
          product_id: selectedData,
          product_conversions: selectedData.product_conversions
        }
      ]
    ]);
    setIsOpenSelectUnit(false);
    setSelectedData(null);
  }, [selectedData]);

  if (isOnFetchingUser) return <VuiLoadingScreen />;

  if (!loggedIn) {
    return <Navigate to={'/auth/login'} replace />;
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <ModalProductItem
        isOpen={isOpenSelectUnit}
        onClose={handleOnCloseSelectUnit}
        selectedData={selectedData}
        customer={pageQuery.customer}
        onSubmit={handleOnSubmitSelectUnit}
      />

      <ModalAskPrintSalesOrder
        isOpen={isOpenAskPrintSalesOrder}
        onClose={() => {
          setIsOpenAskPrintSalesOrder(false);
          setSalesOrderId(undefined);
        }}
        salesOrderId={salesOrderId}
      />

      <div className='sales-order-simple-page'>
        <Row className='so-header'>
          <Col xs={12} md={7}>
            <Row>
              <Col xs={12} md={8}>
                <div className='d-flex gap-2 align-items-center'>
                  <VuiSearch
                    style={{ backgroundColor: 'white' }}
                    value={search}
                    callback={value => setSearch(value)}
                  />
                </div>
              </Col>
              <Col xs={12} md={4}>
                <VuiSelect
                  selectRepository={CategoryRepository}
                  defaultValue={pageQuery.category}
                  onChange={value => {
                    setPageQuery({
                      ...pageQuery,
                      page: 1,
                      category: value
                    });
                  }}
                  isMulti={false}
                  placeholder='Category'
                  clearable
                />
              </Col>
            </Row>
          </Col>
          <Col xs={12} md={5}>
            <div className="d-flex w-100 align-items-center gap-2">
              <div className="w-100">
                <VuiSelect
                  selectRepository={CustomerRepository}
                  defaultValue={pageQuery.customer}
                  onChange={value => {
                    setPageQuery({
                      ...pageQuery,
                      page: 1,
                      customer: value
                    });
                  }}
                  isMulti={false}
                  placeholder='Customer'
                />
              </div>

              <ButtonAddCustomer
                onSave={(value) => {
                  setPageQuery({
                    ...pageQuery,
                    page: 1,
                    customer: value
                  })
                }}
              />

              <ButtonBack />
            </div>
          </Col>
        </Row>

        <br />

        <Row>
          <Col xs={12} md={7}>
            {products.length ? (
              <div id='product-section-scrollable'>
                <InfiniteScroll
                  dataLength={products.length}
                  next={handleNextProduct}
                  hasMore={hasMore}
                  scrollThreshold={0.95}
                  loader={
                    <div style={{ textAlign: 'center', padding: '10px 0' }}>
                      <LoadingIndication />
                    </div>
                  }
                  scrollableTarget='product-section-scrollable'
                >
                  <div className='products-section'>
                    {products.map(p => (
                      <ProductItem key={p?.id} product={p} onClick={() => handleAddProductToCart(p)} />
                    ))}
                  </div>
                </InfiniteScroll>
              </div>
            ) : !products.length && isFetchingProduct ? (
              <div className='product-section-empty'>
                <LoadingIndication />
              </div>
            ) : !products.length && !pageQuery.customer ? (
              <div className='product-section-empty'>
                <div style={{ color: 'red' }} className='section-empty-text'>
                  Please select customer first
                </div>
              </div>
            ) : (
              <div className='product-section-empty'>
                <div>
                  <div className='section-empty-icon'>
                    <FontAwesomeIcon icon={faShoppingBag} />
                  </div>
                  <div className='section-empty-text'>No products available</div>
                </div>
              </div>
            )}
          </Col>

          <Col xs={12} md={5}>
            <div className='cart-section'>
              {carts.length ? (
                <Row className='so-cart-items'>
                  <Col xs={12}>
                    {carts.map((item, index) => (
                      <SalesOrderCartItem
                        key={`sales_order_cart_item_${index}`}
                        data={item}
                        index={index}
                        customer={pageQuery.customer}
                        setData={setCarts}
                        handleRemove={() => {
                          const clone = [...carts];
                          clone.splice(index, 1);
                          setCarts(clone);
                        }}
                      />
                    ))}
                  </Col>
                </Row>
              ) : (
                <div className='d-flex h-100 align-items-center justify-content-center'>
                  <div className='text-center'>
                    <div className='section-empty-icon'>
                      <FontAwesomeIcon icon={faCartArrowDown} />
                    </div>
                    <div className='section-empty-text'>Your cart is empty</div>
                  </div>
                </div>
              )}

              <div className='cart-action-summary'>
                {errors && (
                  <div className='alert alert-danger mb-2' role='alert' style={{ paddingTop: '8px', paddingBottom: '8px' }}>
                    <div className='d-flex align-items-start gap-2'>
                      <div>
                        <FontAwesomeIcon icon={faExclamationCircle} style={{ marginRight: 6 }} />
                      </div>
                      <div>
                        {Object.keys(errors).map((key: string) => (
                          <div style={{ fontSize: 11 }}>{errors[key]?.message}</div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                <div className='d-flex gap-2 align-items-center mt-2 mb-2'>
                  <ButtonAddNote
                    note={note}
                    onSave={value => {
                      setNote(value);
                    }}
                  />
                  <ButtonAddDisc
                    discount={discount}
                    onSave={value => {
                      setDiscount(value);
                    }}
                  />
                </div>

                {renderSummary}

                <div className='d-flex align-items-center gap-2'>
                  <VuiButton
                    disabled={!pageQuery.customer || !carts.length}
                    label='Pay Now'
                    loading={submitLoading}
                    onClick={handleOnPayNow}
                    customClass='btn-pay now'
                  />
                  <ButtonSave disabled={!pageQuery.customer || !carts.length} handlePay={handleOnSave} loading={submitSaveLoading} />
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </>
  );
};

export default SalesOrderSimplePage;
